import React from 'react';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    createParkingGatewayFormSchema,
    type CreateParkingGatewayFormSchemaValues,
} from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useCreateParkingGateway';
import { type ParkingGateways } from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useParkingGateways';
import { useUpdateParkingGateway } from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useUpdateParkingGateway';
import ParkingGatewayForm from 'src/features/parkingManagement/components/EntryExitGateways/ParkingGatewayForm';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

type EditParkingGatewayModalProps = {
    isEditParkingGatewayModalOpen: boolean;
    closeEditParkingGatewayModal: (isOpen: boolean) => void;
    selectedParkingGateway: ParkingGateways;
    refetch: () => void;
};

const EditParkingGatewayModal = ({
    isEditParkingGatewayModalOpen,
    closeEditParkingGatewayModal,
    selectedParkingGateway,
    refetch,
}: EditParkingGatewayModalProps) => {
    const { formatMessage } = useIntl();

    const form = useForm<CreateParkingGatewayFormSchemaValues>({
        resolver: zodResolver(createParkingGatewayFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    const { isDirty, isValid } = form.formState;

    React.useEffect(() => {
        if (selectedParkingGateway) {
            form.reset({
                parking: selectedParkingGateway.parking,
                gateway_type: selectedParkingGateway.gateway_type,
                name: selectedParkingGateway.name,
                address: selectedParkingGateway.address,
                longitude: selectedParkingGateway.coordinates?.coordinates[0]?.toString(),
                latitude: selectedParkingGateway.coordinates?.coordinates[1]?.toString(),
                max_height_cm: selectedParkingGateway.max_height_cm,
                max_width_cm: selectedParkingGateway.max_width_cm,
            });
        }
    }, [selectedParkingGateway]);

    const { mutate } = useUpdateParkingGateway({
        onSuccess: () => {
            void refetch();
            toast({
                title: formatMessage(parkingManagementMessages.editPointSuccess),
                description: formatMessage(parkingManagementMessages.editPointDescriptionSuccess),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit: SubmitHandler<CreateParkingGatewayFormSchemaValues> = (
        values: CreateParkingGatewayFormSchemaValues,
    ) => {
        const longitude = Number(values.longitude);
        const latitude = Number(values.latitude);

        if (isNaN(longitude) || isNaN(latitude)) {
            throw new Error(formatMessage(parkingManagementMessages.invalidCoordinates));
        }

        mutate({
            id: selectedParkingGateway.id,
            ...values,
            coordinates: {
                type: 'Point',
                coordinates: [longitude, latitude],
            },
        });
        closeEditParkingGatewayModal(!isEditParkingGatewayModalOpen);
    };

    return (
        <Dialog open={isEditParkingGatewayModalOpen} onOpenChange={closeEditParkingGatewayModal}>
            <DialogContent>
                <DialogContent>
                    <DialogHeader>
                        <div className="flex flex-row gap-2">
                            <SquareIcon icon={faEdit} />
                            <div>
                                <DialogTitle>{formatMessage(parkingManagementMessages.editEntryExit)}</DialogTitle>
                                <DialogDescription>
                                    {formatMessage(parkingManagementMessages.editPointDescription)}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <ParkingGatewayForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                            disabled={!isDirty || !isValid}
                        >
                            {formatMessage(generalMessages.edit)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogContent>
        </Dialog>
    );
};

export default EditParkingGatewayModal;
